/**
 * Integridade — tentativas de combinar serviços fora da Dozy.
 *
 * A plataforma oculta contactos automaticamente e suspende quem insiste
 * (5 tentativas para trabalhadores, 10 para clientes, em 30 dias). Este ecrã
 * existe por causa do que o automatismo não sabe: distinguir quem tentou
 * fugir à comissão de quem escreveu um número por outro motivo qualquer.
 *
 * Sem isto, levantar uma suspensão injusta obrigava a ir à base de dados.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { ShieldAlert, Loader2, Undo2, ChevronRight, Search } from 'lucide-react';
import api from '../utils/api';
import { useToast } from '../hooks/useToast';

const dataHora = (v) => (v ? new Date(v).toLocaleString('pt-PT') : '—');

const MOTIVOS = {
  telefone: 'Telefone',
  'telefone-disfarcado': 'Telefone disfarçado',
  email: 'Email',
  ligacao: 'Ligação externa',
  'rede-social': 'Rede social',
  utilizador: 'Utilizador',
};

const CONTEXTOS = {
  mensagem: 'Mensagem',
  descricao: 'Descrição do pedido',
  candidatura: 'Proposta',
};

const IntegrityPage = () => {
  const { success, error } = useToast();
  const [dados, setDados] = useState({ utilizadores: [], limites: {}, janela_dias: 30 });
  const [loading, setLoading] = useState(true);
  const [apenasSuspensos, setApenasSuspensos] = useState(false);
  const [procura, setProcura] = useState('');
  const [detalhe, setDetalhe] = useState(null);     // utilizador aberto
  const [tentativas, setTentativas] = useState([]);
  const [aCarregarDetalhe, setACarregarDetalhe] = useState(false);
  const [busy, setBusy] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/bypass', {
        params: apenasSuspensos ? { suspensos: 1 } : {},
      });
      setDados(data || { utilizadores: [] });
    } catch {
      error('Erro ao carregar as tentativas');
    } finally {
      setLoading(false);
    }
  }, [apenasSuspensos, error]);

  useEffect(() => { carregar(); }, [carregar]);

  const abrir = async (u) => {
    setDetalhe(u);
    setACarregarDetalhe(true);
    try {
      const { data } = await api.get(`/admin/bypass/${u.user_id}`);
      setTentativas(Array.isArray(data) ? data : []);
    } catch {
      error('Erro ao carregar o histórico');
      setTentativas([]);
    } finally {
      setACarregarDetalhe(false);
    }
  };

  const perdoar = async (u) => {
    const aviso = u.status === 'suspended'
      ? `Reactivar a conta de ${u.full_name} e perdoar ${u.a_contar} tentativa(s)?`
      : `Perdoar ${u.a_contar} tentativa(s) de ${u.full_name}? O contador volta a zero.`;
    if (!window.confirm(aviso)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/bypass/${u.user_id}/perdoar`);
      success(data.message || 'Feito.');
      setDetalhe(null);
      await carregar();
    } catch (e) {
      error(e.response?.data?.message || 'Não foi possível levantar a suspensão');
    } finally {
      setBusy(false);
    }
  };

  const visiveis = dados.utilizadores.filter((u) => {
    if (!procura.trim()) return true;
    const q = procura.toLowerCase();
    return (u.full_name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
  });

  const suspensos = dados.utilizadores.filter((u) => u.status === 'suspended').length;

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <ShieldAlert className="w-6 h-6 text-amber-400" /> Integridade da plataforma
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Tentativas de partilhar contactos ou combinar serviços fora da Dozy.
            Suspensão automática: {dados.limites?.worker ?? 5} tentativas para profissionais,{' '}
            {dados.limites?.client ?? 10} para clientes, em {dados.janela_dias ?? 30} dias.
          </p>
        </div>
        {suspensos > 0 && (
          <span className="px-3 py-1.5 rounded-lg bg-red-500/15 text-red-400 border border-red-500/30 text-sm font-semibold">
            {suspensos} conta{suspensos > 1 ? 's' : ''} suspensa{suspensos > 1 ? 's' : ''}
          </span>
        )}
      </div>

      <div className="flex gap-3 flex-wrap items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={procura}
            onChange={(e) => setProcura(e.target.value)}
            placeholder="Procurar por nome ou email"
            className="w-full pl-9 pr-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 text-sm outline-none focus:border-blue-500"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
          <input
            type="checkbox"
            checked={apenasSuspensos}
            onChange={(e) => setApenasSuspensos(e.target.checked)}
            className="accent-blue-500"
          />
          Só contas suspensas
        </label>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
        </div>
      ) : visiveis.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <ShieldAlert className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="font-medium text-slate-300">Nada a assinalar</p>
          <p className="text-sm">Ninguém tentou levar negócio para fora da plataforma.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-800 hidden md:block">
          <table className="w-full text-sm">
            <thead className="bg-slate-800/60">
              <tr className="text-left text-slate-400 text-xs uppercase tracking-wider">
                <th className="px-4 py-3">Utilizador</th>
                <th className="px-4 py-3">Papel</th>
                <th className="px-4 py-3 text-right">A contar</th>
                <th className="px-4 py-3 text-right">Histórico</th>
                <th className="px-4 py-3">Última</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {visiveis.map((u) => {
                const limite = u.limite ?? '—';
                const perto = u.limite && u.a_contar >= u.limite - 1;
                return (
                  <tr key={u.user_id} className="border-t border-slate-800 hover:bg-slate-800/40">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-200">{u.full_name}</div>
                      <div className="text-xs text-slate-500">{u.email}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-400">
                      {u.role === 'worker' ? 'Profissional' : u.role === 'client' ? 'Cliente' : u.role}
                    </td>
                    <td className={`px-4 py-3 text-right font-bold tabular-nums ${perto ? 'text-amber-400' : 'text-slate-300'}`}>
                      {u.a_contar}<span className="text-slate-600 font-normal"> / {limite}</span>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-500">
                      {u.total_historico}
                      {u.perdoadas > 0 && <span className="text-slate-600"> ({u.perdoadas} perdoada{u.perdoadas > 1 ? 's' : ''})</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-400 whitespace-nowrap">{dataHora(u.ultima)}</td>
                    <td className="px-4 py-3">
                      {u.status === 'suspended' ? (
                        <span className="px-2 py-1 rounded-md text-xs font-semibold bg-red-500/15 text-red-400 border border-red-500/30">
                          Suspensa
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded-md text-xs font-semibold bg-green-500/15 text-green-400 border border-green-500/30">
                          Activa
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => abrir(u)}
                        className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 text-sm font-medium"
                      >
                        Ver <ChevronRight className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Cartoes — telemovel. Seccao 15: tabelas densas no desktop, listas no
          telemovel. Sete colunas nao cabem em 375px. */}
      {!loading && visiveis.length > 0 && (
        <div className="md:hidden flex flex-col gap-2">
          {visiveis.map((u) => {
            const perto = u.limite && u.a_contar >= u.limite - 1;
            return (
              <button
                key={u.user_id}
                type="button"
                onClick={() => abrir(u)}
                className="w-full text-left p-4 rounded-xl border border-slate-800 bg-slate-800/40 active:bg-slate-800"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-200 break-words">{u.full_name}</div>
                    <div className="text-xs text-slate-500 break-all">{u.email}</div>
                  </div>
                  {u.status === 'suspended' ? (
                    <span className="shrink-0 px-2 py-1 rounded-md text-xs font-semibold bg-red-500/15 text-red-400 border border-red-500/30">
                      Suspensa
                    </span>
                  ) : (
                    <span className="shrink-0 px-2 py-1 rounded-md text-xs font-semibold bg-green-500/15 text-green-400 border border-green-500/30">
                      Activa
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-4 mt-3 text-xs text-slate-400 flex-wrap">
                  <span>{u.role === 'worker' ? 'Profissional' : u.role === 'client' ? 'Cliente' : u.role}</span>
                  <span className={perto ? 'text-amber-400 font-bold' : 'text-slate-300 font-bold'}>
                    {u.a_contar} / {u.limite ?? '—'} tentativas
                  </span>
                  <span className="ml-auto">{dataHora(u.ultima)}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Detalhe — o que a pessoa escreveu, para se poder julgar */}
      {detalhe && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setDetalhe(null)}>
          <div
            className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-5">
              <div className="flex-1">
                <h2 className="text-lg font-bold text-white">{detalhe.full_name}</h2>
                <p className="text-sm text-slate-400">
                  {detalhe.email} · {detalhe.role === 'worker' ? 'Profissional' : 'Cliente'} ·{' '}
                  {detalhe.a_contar} de {detalhe.limite ?? '—'} tentativas a contar
                </p>
              </div>
              <button onClick={() => setDetalhe(null)} className="text-slate-500 hover:text-slate-300 text-xl leading-none">×</button>
            </div>

            {aCarregarDetalhe ? (
              <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-500" /></div>
            ) : (
              <ul className="space-y-3">
                {tentativas.map((t) => (
                  <li
                    key={t.id}
                    className={`p-3 rounded-xl border ${t.perdoado_em ? 'border-slate-800 opacity-50' : 'border-slate-700'} bg-slate-800/40`}
                  >
                    <div className="flex items-center gap-2 flex-wrap text-xs mb-1.5">
                      <span className="px-2 py-0.5 rounded bg-slate-700 text-slate-300 font-medium">
                        {CONTEXTOS[t.contexto] || t.contexto}
                      </span>
                      {(t.motivos || []).map((m) => (
                        <span key={m} className="px-2 py-0.5 rounded bg-amber-500/15 text-amber-400 font-medium">
                          {MOTIVOS[m] || m}
                        </span>
                      ))}
                      {t.request_id && <span className="text-slate-500">pedido #{t.request_id}</span>}
                      <span className="text-slate-500 ml-auto">{dataHora(t.created_at)}</span>
                    </div>
                    {/* Texto original, antes de mascarar — é o que permite
                        distinguir má-fé de descuido. */}
                    <p className="text-sm text-slate-300 whitespace-pre-wrap break-words">{t.excerto}</p>
                    {t.perdoado_em && (
                      <p className="text-xs text-slate-500 mt-1.5">Perdoada em {dataHora(t.perdoado_em)}</p>
                    )}
                  </li>
                ))}
                {tentativas.length === 0 && (
                  <li className="text-center text-slate-500 py-8">Sem tentativas registadas.</li>
                )}
              </ul>
            )}

            {detalhe.a_contar > 0 && (
              <div className="mt-6 pt-5 border-t border-slate-800">
                <button
                  onClick={() => perdoar(detalhe)}
                  disabled={busy}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-semibold"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Undo2 className="w-4 h-4" />}
                  {detalhe.status === 'suspended' ? 'Reactivar conta e perdoar tentativas' : 'Perdoar tentativas'}
                </button>
                <p className="text-xs text-slate-500 mt-2 text-center">
                  As tentativas continuam no registo, mas deixam de contar para o limite.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegrityPage;
